/* =====================================================================
   serene.js — site-wide Serene Heat behaviours.

   Replaces several duplicated inline <script> blocks across sections with
   a single deferred, delegated module. Idempotent: if a behaviour is set
   up by another section script first, the same DOM listener won't fire
   twice (we tag elements once they're wired).

   Bundled behaviours:
     1. FAQ / PDP feature accordion (delegated click + keyboard toggle,
        with proper aria-expanded / aria-controls).
     2. Hamburger nav toggle with aria-expanded / aria-controls.
     3. SereneA11y.announce(text) — write to the global polite live region.
     4. Reduced-motion friendly smooth-scroll for in-page anchor links.
   ===================================================================== */

(function () {
  'use strict';

  if (window.__SereneInit) return;
  window.__SereneInit = true;

  var prefersReduce = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --------------- 1. Accessible accordion (FAQ + PDP features) --------------- */

  function wireAccordions(root) {
    root = root || document;

    // FAQ items (.faq-item > .faq-q + .faq-a)
    var faqItems = root.querySelectorAll('.faq-item');
    faqItems.forEach(function (item, idx) {
      if (item.__sereneFaqWired) return;
      item.__sereneFaqWired = true;

      var btn = item.querySelector('.faq-q');
      var panel = item.querySelector('.faq-a');
      if (!btn || !panel) return;

      // Pair button with panel via aria-controls / aria-expanded.
      if (!panel.id) {
        panel.id = 'serene-faq-panel-' + Math.random().toString(36).slice(2, 9);
      }
      btn.setAttribute('aria-controls', panel.id);
      btn.setAttribute('aria-expanded', item.classList.contains('open') ? 'true' : 'false');
      panel.setAttribute('role', 'region');

      btn.addEventListener('click', function () {
        var list = item.parentElement;
        var wasOpen = item.classList.contains('open');

        // Close siblings (single-open behaviour, matches existing UX).
        if (list) {
          list.querySelectorAll('.faq-item.open').forEach(function (other) {
            if (other !== item) {
              other.classList.remove('open');
              var otherBtn = other.querySelector('.faq-q');
              if (otherBtn) otherBtn.setAttribute('aria-expanded', 'false');
            }
          });
        }

        if (wasOpen) {
          item.classList.remove('open');
          btn.setAttribute('aria-expanded', 'false');
        } else {
          item.classList.add('open');
          btn.setAttribute('aria-expanded', 'true');
        }
      });
    });

    // PDP feature accordion (.pdp-feat-item > .pdp-feat-q + .pdp-feat-a)
    var feats = root.querySelectorAll('.pdp-feat-item');
    feats.forEach(function (item) {
      if (item.__sereneFeatWired) return;
      item.__sereneFeatWired = true;

      var btn = item.querySelector('.pdp-feat-q');
      var panel = item.querySelector('.pdp-feat-a');
      if (!btn || !panel) return;

      if (!panel.id) {
        panel.id = 'serene-feat-panel-' + Math.random().toString(36).slice(2, 9);
      }
      btn.setAttribute('aria-controls', panel.id);
      btn.setAttribute('aria-expanded', item.classList.contains('open') ? 'true' : 'false');
      panel.setAttribute('role', 'region');

      btn.addEventListener('click', function () {
        var open = item.classList.toggle('open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }

  /* --------------- 2. Hamburger nav --------------- */

  function wireHamburger() {
    var hamburger = document.querySelector('.serene-nav-section .hamburger');
    var navLinks = document.querySelector('.serene-nav-section .nav-links');
    if (!hamburger || !navLinks || hamburger.__sereneNavWired) return;
    hamburger.__sereneNavWired = true;

    // Pair button + region.
    if (!navLinks.id) navLinks.id = 'serene-nav-links';
    hamburger.setAttribute('aria-controls', navLinks.id);
    hamburger.setAttribute('aria-expanded', 'false');
    if (!hamburger.getAttribute('aria-label')) {
      hamburger.setAttribute('aria-label', 'Toggle navigation menu');
    }

    function toggle() {
      var open = hamburger.classList.toggle('open');
      navLinks.classList.toggle('open', open);
      hamburger.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    hamburger.addEventListener('click', toggle);
    hamburger.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggle();
      }
    });

    // Close on Escape when open.
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && hamburger.classList.contains('open')) {
        toggle();
        hamburger.focus();
      }
    });
  }

  /* --------------- 3. Polite live-region helper --------------- */

  function announce(message) {
    var region = document.querySelector('[data-serene-live]');
    if (!region) return;
    // Clear then set so screen readers re-announce identical text.
    region.textContent = '';
    setTimeout(function () { region.textContent = message; }, 30);
  }
  window.SereneA11y = window.SereneA11y || {};
  window.SereneA11y.announce = announce;

  /* --------------- 4. Smooth in-page anchor scroll --------------- */

  document.addEventListener('click', function (e) {
    var link = e.target.closest('a[href^="#"]');
    if (!link) return;
    var href = link.getAttribute('href');
    if (!href || href === '#' || href.length < 2) return;
    // Allow Shopify control links / explicit data-no-scroll.
    if (link.hasAttribute('data-no-scroll')) return;
    var target = document.getElementById(href.slice(1));
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({
      behavior: prefersReduce ? 'auto' : 'smooth',
      block: 'start'
    });
    // Move focus to the target for keyboard users.
    var prevTabIndex = target.getAttribute('tabindex');
    if (prevTabIndex === null) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    if (prevTabIndex === null) {
      // Restore after blur so tab order isn't permanently changed.
      target.addEventListener('blur', function once() {
        target.removeAttribute('tabindex');
        target.removeEventListener('blur', once);
      });
    }
  });

  /* --------------- Boot --------------- */

  function init() {
    wireAccordions(document);
    wireHamburger();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-wire after Shopify section render (theme editor + cart drawer refresh).
  document.addEventListener('shopify:section:load', function (e) {
    wireAccordions(e.target || document);
    wireHamburger();
  });
})();
